import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import ActivityTracker from './ActivityTracker';
import InstallAppButton from './InstallAppButton';
import PublicFooter from './PublicFooter';
import { 
  LayoutDashboard, 
  FileImage, 
  Palette, 
  User, 
  LogOut, 
  Menu, 
  X,
  Users,
  BarChart3,
  Settings,
  Key,
  ShieldCheck,
  UploadCloud,
  MoreHorizontal
} from 'lucide-react';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { user, logout, isAdmin, isSuperAdmin, supportSession, endSupportAccess } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    if (supportSession) { endSupportAccess(); return; }
    logout();
    navigate('/login');
  };

  const customerNavItems = [
    { path: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', mobileLabel: 'Home' },
    { path: '/flyers', icon: FileImage, label: 'Flyer Library', mobileLabel: 'Library' },
    { path: '/branding', icon: Palette, label: 'My Branding', mobileLabel: 'Brand' },
    { path: '/profile', icon: User, label: 'Profile', mobileLabel: 'Profile' },
  ];

  const adminNavItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/users', icon: Users, label: 'Users' },
    ...(isSuperAdmin ? [{ path: '/admin/team', icon: ShieldCheck, label: 'Admin Access' }] : []),
    { path: '/admin/invite-codes', icon: Key, label: 'Invite Codes' },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
    { path: '/admin/flyers', icon: FileImage, label: 'Manage Flyers' },
    ...(isSuperAdmin ? [{ path: '/admin/flyer-imports', icon: UploadCloud, label: 'Import Studio' }] : []),
    { path: '/admin/settings', icon: Settings, label: 'Settings' },
  ];

  const navItems = isAdmin ? adminNavItems : customerNavItems;
  const mobileNavItems = isAdmin
    ? [
        adminNavItems[0],
        adminNavItems[1],
        adminNavItems.find(item => item.path === '/admin/analytics')
      ].filter(Boolean)
    : customerNavItems;

  const isPathActive = (path) => (
    location.pathname === path
    || (path === '/flyers' && location.pathname.startsWith('/flyers/'))
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <ActivityTracker />

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        ></div>
      )}

      {/* Sidebar */}
      <aside className={`
        app-sidebar-offset fixed bottom-0 left-0 z-50 w-64 bg-white shadow-xl transform transition-transform duration-300 ease-in-out
        flex flex-col
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="h-20 flex items-center justify-between px-6 border-b border-gray-100">
          <Link to={isAdmin ? '/admin' : '/dashboard'} className="flex items-center gap-3">
            <div className="w-12 h-12 flex items-center justify-center">
              <img src="/ttic-logo.png" alt="TTIC" className="w-full h-full object-contain" />
            </div>
            <div>
              <h1 className="font-bold text-primary-500 text-sm leading-tight">TTIC Marketing</h1>
              <p className="text-xs text-gray-400">Hub</p>
            </div>
          </Link>
          <button 
            className="lg:hidden text-gray-400 hover:text-gray-600"
            onClick={() => setSidebarOpen(false)}
          >
            <X size={24} />
          </button>
        </div>

        {/* User info */}
        <div className="px-4 py-4 border-b border-gray-100">
          <div className="bg-gray-50 rounded-xl p-3">
            <p className="font-semibold text-gray-800 text-sm truncate">{user?.companyName}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
            {isAdmin && (
              <span className="inline-block mt-2 px-2 py-0.5 bg-primary-500 text-white text-xs rounded-full">
                {isSuperAdmin ? 'Super Admin' : 'Manager'}
              </span>
            )}
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto p-4 space-y-1">
          {navItems.map((item) => {
            const isActive = isPathActive(item.path);
            const Icon = item.icon;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`sidebar-link ${isActive ? 'active' : ''}`}
                onClick={() => setSidebarOpen(false)}
              >
                <Icon size={20} />
                <span>{item.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Logout button */}
        <div className="p-4 border-t border-gray-100">
          <button
            onClick={handleLogout}
            className="btn-danger-ghost w-full"
          >
            <LogOut size={20} />
            <span>{supportSession ? 'End support access' : 'Sign Out'}</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="app-shell-offset lg:ml-64">
        {/* Top bar */}
        <header style={{ top: 'var(--support-banner-height, 0px)' }} className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-gray-100 bg-white/95 px-3 backdrop-blur sm:px-4 lg:px-8">
          <button 
            className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-xl text-gray-600 hover:bg-gray-100 hover:text-gray-800 lg:hidden"
            onClick={() => setSidebarOpen(true)}
            aria-label="Open navigation"
          >
            <Menu size={24} />
          </button>

          <Link to={isAdmin ? '/admin' : '/dashboard'} className="flex items-center gap-2 lg:hidden" aria-label="TTIC Marketing Hub home">
            <img src="/ttic-logo.png" alt="" className="h-9 w-9 object-contain" />
            <span className="hidden text-sm font-bold text-primary-700 sm:inline">Marketing Hub</span>
          </Link>

          <div className="hidden flex-1 lg:block"></div>
          
          <div className="flex items-center gap-2 sm:gap-4">
            <InstallAppButton compact />
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium text-gray-800">{user?.contactName || user?.companyName}</p>
              <p className="text-xs text-gray-500">{isSuperAdmin ? 'Super Administrator' : isAdmin ? 'Manager' : 'Customer'}</p>
            </div>
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <span className="text-primary-500 font-semibold">
                {(user?.companyName || 'U')[0].toUpperCase()}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <main className="px-4 pb-28 pt-4 sm:px-6 lg:p-8">
          {children}
        </main>
        <div className="hidden border-t border-gray-200 bg-white px-6 lg:block">
          <PublicFooter compact />
        </div>
      </div>

      <nav className="mobile-bottom-nav fixed inset-x-0 bottom-0 z-40 border-t border-gray-200 bg-white/95 px-2 shadow-[0_-8px_24px_rgba(15,23,42,0.08)] backdrop-blur lg:hidden" aria-label="Primary navigation">
        <div className="mx-auto flex min-h-16 max-w-lg items-stretch justify-around">
          {mobileNavItems.map(item => {
            const Icon = item.icon;
            const active = isPathActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex min-h-16 min-w-16 flex-1 flex-col items-center justify-center gap-1 rounded-xl px-2 text-[11px] font-semibold transition ${active ? 'text-primary-600' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-800'}`}
                aria-current={active ? 'page' : undefined}
              >
                <Icon size={21} strokeWidth={active ? 2.5 : 2} />
                <span>{item.mobileLabel || item.label}</span>
              </Link>
            );
          })}
          {isAdmin && (
            <button
              type="button"
              onClick={() => setSidebarOpen(true)}
              className="flex min-h-16 min-w-16 flex-1 flex-col items-center justify-center gap-1 rounded-xl px-2 text-[11px] font-semibold text-gray-500 hover:bg-gray-50 hover:text-gray-800"
            >
              <MoreHorizontal size={21} />
              <span>More</span>
            </button>
          )}
        </div>
      </nav>
    </div>
  );
};

export default Layout;
